import { Views } from "@singularsystems/neo-react";
import { AppService, Types } from '../../../App/Services/AppService';
import { Components, ModalUtils, NeoModel } from "@singularsystems/neo-core";
import TradeOrderSearchCriteria from "../Models/Queries/TradeOrderSearchCriteria";
import TradeOrderLookup from "../Models/Lookups/TradeOrderLookup";
import { Data } from "@singularsystems/neo-core";
import UpdateTradeOrderCommand from "../Models/Commands/UpdateTradeOrderCommand";
import ReleaseTradeOrdersCommand from "../Models/Commands/ReleaseTradeOrdersCommand";
import { TradeOrderStatus, getTradeOrderStatusName } from "../Models/Enums/TradeOrderStatus";
import { NotifyUtils } from "@singularsystems/neo-core";
import { TextConstants } from "../../../Common/TextConstants";
import { AuthorisableRequests, TradeOrders } from "../../../AccessControl/Models/Commands/Security/Roles";

@NeoModel
export default class TradeOrderVM extends Views.ViewModelBase {
    // State for UI - no @observable needed with @NeoModel
    public selectedTradeOrderIDs: string[] = [];
    public selectAll: boolean = false;
    // Modal visibility flags
    public showConfirmModal: boolean = false;
    public showRejectModal: boolean = false;
    public showReinstateModal: boolean = false;
    public confirmValidationMessage: string = "";
    public confirmIsFullDisinvestment: boolean = false;
    public confirmTradeOrder: UpdateTradeOrderCommand | null = null;
    public rejectTradeOrder: UpdateTradeOrderCommand | null = null;
    public reinstateTradeOrder: UpdateTradeOrderCommand | null = null;
    // Cache for service provider names
    public serviceProviderLookup = new Map<number, string>();
    // Add a property to store the currency lookup
    public currencyLookup = new Map<number, string>();
    // Add this property to store all service providers for dropdowns
    public allServiceProviders: any[] = [];

    constructor(
        public TradeOrderApiClient = AppService.get(Types.ApiClients.TradeOrderApiClient),
        public serviceProviderApiClient = AppService.get(Types.ApiClients.ServiceProviderApiClient),
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private navigation = AppService.get(Types.Neo.Routing.NavigationHelper),
        public authorisationService = AppService.get(Types.Neo.Security.AuthorisationService)
    ) {
        super(taskRunner);
        this.loadAllServiceProviders();
    }

    private isConfirmedAmountValid(confirmedAmount: number | null | undefined): boolean {
        return confirmedAmount != null && confirmedAmount !== 0 && !Number.isNaN(confirmedAmount);
    }

    public clearConfirmValidationMessage() {
        if (this.confirmTradeOrder && this.isConfirmedAmountValid(this.confirmTradeOrder.confirmedAmount)) {
            this.confirmValidationMessage = "";
        }
    }

    public tradeOrderSearchCriteria = new TradeOrderSearchCriteria();

    public pageManager = new Data.PageManager(
        this.tradeOrderSearchCriteria,
        TradeOrderLookup,
        (request) => this.TradeOrderApiClient.getTradeOrdersPagedList(request),
        {
            pageSize: 10,
            sortBy: "cycleDate",
            sortAscending: false,
            fetchInitial: true,
            initialTaskRunner: this.taskRunner
        }
    );

    // Role-based permission checks
    public get canRelease(): boolean {
        return this.authorisationService.hasRole(TradeOrders.Release) ||
            this.authorisationService.hasRole(AuthorisableRequests.Access);
    }

    public get canConfirm(): boolean {
        return this.authorisationService.hasRole(TradeOrders.Confirm) ||
            this.authorisationService.hasRole(AuthorisableRequests.Access);
    }

    public get canReject(): boolean {
        return this.authorisationService.hasRole(TradeOrders.Reject) ||
            this.authorisationService.hasRole(AuthorisableRequests.Access);
    }

    public get canReinstate(): boolean {
        return this.authorisationService.hasRole(TradeOrders.Reinstate) ||
            this.authorisationService.hasRole(AuthorisableRequests.Access);
    }

    public get canDownload(): boolean {
        return this.authorisationService.hasRole(TradeOrders.Download) ||
            this.authorisationService.hasRole(AuthorisableRequests.Access);
    }

    public get canViewAll(): boolean {
        return this.authorisationService.hasRole(TradeOrders.View) ||
            this.authorisationService.hasRole(AuthorisableRequests.Access);
    }

    // Check if all selectable items are selected
    public isAllSelected(): boolean {
        const allItems = Array.from(this.pageManager.data || []);
        const selectableItems = allItems.filter(item =>
            item.tradeOrderStatusId === TradeOrderStatus.Pending ||
            item.tradeOrderStatusName === "Pending" ||
            item.tradeOrderStatusName === "PROPOSED"
        );
        return selectableItems.length > 0 &&
            selectableItems.every(item => this.selectedTradeOrderIDs.includes(String(item.tradeOrderId)));
    }

    // Select/deselect a single item
    public handleSelect = (item: TradeOrderLookup, checked: boolean) => {
        const id = String(item.tradeOrderId);
        if (checked) {
            if (!this.selectedTradeOrderIDs.includes(id)) {
                this.selectedTradeOrderIDs.push(id);
            }
        } else {
            this.selectedTradeOrderIDs = this.selectedTradeOrderIDs.filter(existingId => existingId !== id);
        }
        this.selectAll = this.isAllSelected();
    };
    // Select/deselect all selectable items
    public handleSelectAll(checked: boolean) {
        // Get all items from all pages
        const allItems = Array.from(this.pageManager.data || []);
        // Filter for selectable items (Pending or PROPOSED)
        const selectableItems = allItems.filter(item =>
            item.tradeOrderStatusId === TradeOrderStatus.Pending ||
            item.tradeOrderStatusName === "Pending" ||
            item.tradeOrderStatusName === "PROPOSED"
        );
        if (checked) {
            this.selectedTradeOrderIDs = selectableItems.map(item => String(item.tradeOrderId));
            this.selectAll = true;
        } else {
            this.selectedTradeOrderIDs = [];
            this.selectAll = false;
        }
    }

    public handleBulkRelease = async () => {
        if (!this.canRelease) {
            NotifyUtils.addWarning("Access Denied", "You do not have permission to release trade orders.", 4);
            return;
        }

        const allItems = Array.from(this.pageManager.data || []);
        const selectedIds = allItems
            .filter(item => this.selectedTradeOrderIDs.includes(String(item.tradeOrderId)))
            .map(item => item.tradeOrderId);

        await this.taskRunner.run(async () => {
            const command = new ReleaseTradeOrdersCommand();
            command.tradeOrderIds = selectedIds;

            const releaseResult = await this.TradeOrderApiClient.releaseTradeOrders(command);

            if (releaseResult.data.success) {
                const message = typeof releaseResult.data.data === 'string' ? releaseResult.data.data : "Trade orders released successfully.";
                NotifyUtils.addSuccess(TextConstants.Titles.Success, message, 4);
            } else {
                NotifyUtils.addWarning("Warning", releaseResult.data.error || "Some trade orders failed to release.", 4);
            }

            await this.pageManager.refreshData();
            this.selectedTradeOrderIDs = [];
            this.selectAll = false;
        });
    };

    public handleBulkDownload = async () => {
        if (!this.canDownload) {
            NotifyUtils.addWarning("Access Denied", "You do not have permission to download trade orders.", 4);
            return;
        }

        await this.taskRunner.run(async () => {
            const allItems: TradeOrderLookup[] = [];
            const originalPageNumber = this.pageManager.pageNo;

            try {
                const totalPages = this.pageManager.totalPages;

                if (totalPages === 0) {
                    NotifyUtils.addWarning("No Results", "No trade orders found to download.", 4);
                    return;
                }

                // Collect all items from all pages
                for (let page = 1; page <= totalPages; page++) {
                    this.pageManager.pageNo = page;
                    await this.pageManager.refreshData();

                    const pageItems = Array.from(this.pageManager.data || []);
                    allItems.push(...pageItems);
                }

                if (allItems.length === 0) {
                    NotifyUtils.addWarning("No Results", "No trade orders found to download.", 4);
                    return;
                }

                // Generate CSV content
                NotifyUtils.addInfo("Info", `Downloading ${allItems.length} trade order(s) from ${totalPages} page(s)...`, 4);
                this.downloadAsCSV(allItems);

            } finally {
                this.pageManager.pageNo = originalPageNumber;
                await this.pageManager.refreshData();
            }
        });
    };

    private downloadAsCSV(items: TradeOrderLookup[]) {
        const headers = [
            "Trade Order ID",
            "Message Type Code",
            "Message Type Name",
            "Type Code",
            "Trade Description",
            "Stockbroker Code",
            "Stockbroker Name",
            "Fund Code",
            "Fund Name",
            "Bulked Investor Code",
            "Bulked Investor Name",
            "Product Code",
            "Product Name",
            "Account No",
            "Reference No",
            "Percent",
            "Units",
            "Currency",
            "Amount",
            "Cycle Date",
            "Mode Code",
            "Fund Reference 1",
            "Status",
            "Note"
        ];

        const rows = items.map(item => [
            this.escapeCSV(String(item.tradeOrderId)),
            this.escapeCSV(item.messageTypeCode),
            this.escapeCSV(item.messageTypeName),
            this.escapeCSV(item.typeCode),
            this.escapeCSV(item.tradeDescription),
            this.escapeCSV(item.stockBrokerCode),
            this.escapeCSV(item.stockBrokerName),
            this.escapeCSV(item.fundCode),
            this.escapeCSV(item.fundName),
            this.escapeCSV(item.bulkedInvestorCode),
            this.escapeCSV(item.bulkedInvestorName),
            this.escapeCSV(item.productCode),
            this.escapeCSV(item.productName),
            this.escapeCSV(item.accountNumber),
            this.escapeCSV(item.referenceNumber),
            item.percent || "",
            item.units || "",
            this.escapeCSV(item.currency),
            item.amount || "",
            item.cycleDate ? new Date(item.cycleDate).toLocaleDateString() : "",
            this.escapeCSV(item.modeCode),
            this.escapeCSV(item.fundReference),
            getTradeOrderStatusName(item.tradeOrderStatusId),
            this.escapeCSV(item.note)
        ]);

        // Combine headers and rows
        const csvContent = [
            headers.join(","),
            ...rows.map(row => row.join(","))
        ].join("\n");

        // Create blob and download
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);

        link.setAttribute("href", url);
        link.setAttribute("download", `TradeOrders_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = "hidden";

        document.body.appendChild(link);
        try {
            link.click();
            NotifyUtils.addSuccess("Success", "CSV file downloaded successfully.", 4);
        } catch (error) {
            NotifyUtils.addWarning("Error", `Error downloading CSV file: ${error}`, 4);
        }
        document.body.removeChild(link);
    }

    // Escape CSV values that contain commas, quotes, or newlines
    private escapeCSV(value: string | null | undefined): string {
        if (!value) return "";

        const stringValue = String(value);

        // If value contains comma, quote, or newline, wrap in quotes and escape quotes
        if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
            return `"${stringValue.replace(/"/g, '""')}"`;
        }

        return stringValue;
    }

    // Simple lookup method
    public getServiceProviderName(id: number | null | undefined): string {
        if (!id) return "";
        return this.serviceProviderLookup.get(id) || `Provider ${id}`;
    }

    // ServiceProvider Name lookup using the cache from loadAllServiceProviders
    public getServiceProviderNameFromCache(id: number): string {
        return this.serviceProviderLookup.get(id) || `Service Provider ${id}`;
    }

    // ==================== CONFIRM MODAL ====================
    public showConfirm = (item: TradeOrderLookup) => {
        if (!this.canConfirm) {
            NotifyUtils.addWarning("Access Denied", "You do not have permission to confirm trade orders.", 4);
            return;
        }
        this.confirmTradeOrder = new UpdateTradeOrderCommand();
        this.confirmIsFullDisinvestment = item.percent === 100;
        this.confirmTradeOrder.tradeOrderId = item.tradeOrderId;
        this.confirmTradeOrder.tradeOrderStatus = TradeOrderStatus.Confirmed;
        this.confirmTradeOrder.amount = item.amount;
        this.confirmTradeOrder.isSent = false;
        this.confirmTradeOrder.confirmedAmount = item.confirmedAmount;
        if (item.note) {
            this.confirmTradeOrder.note = item.note;
        }

        this.showConfirmModal = true;
    };

    // Close the confirm modal
    public closeConfirm = () => {
        this.confirmTradeOrder = null;
        this.confirmIsFullDisinvestment = false;
        this.confirmValidationMessage = "";
        this.clearUploadedFile();
        this.uploadedFiles = undefined;
        this.showConfirmModal = false;
    };

    public submitConfirm = async () => {
        if (!this.confirmTradeOrder) return;
        const command = this.confirmTradeOrder;

        if (!this.isConfirmedAmountValid(command.confirmedAmount)) {
            this.confirmValidationMessage = TextConstants.Notifications.ConfirmedAmountRequired;
            return;
        }

        await this.taskRunner.run(async () => {
            if (this.uploadedFiles && this.uploadedFiles.length > 0 && !await this.tryUploadAttachment(command.tradeOrderId, this.uploadedFiles[0])) {
                return;
            }
            await this.TradeOrderApiClient.updateTradeOrder(command.toJSObject());
            await this.pageManager.refreshData();
            this.closeConfirm();
            NotifyUtils.addSuccess(TextConstants.Titles.Success, "Trade order confirmed successfully.", 4);
        });
    };

    // ==================== REJECT MODAL ====================
    public showReject = (item: TradeOrderLookup) => {
        if (!this.canReject) {
            NotifyUtils.addWarning("Access Denied", "You do not have permission to reject trade orders.", 4);
            return;
        }

        this.rejectTradeOrder = new UpdateTradeOrderCommand();
        this.rejectTradeOrder.tradeOrderId = item.tradeOrderId;
        this.rejectTradeOrder.tradeOrderStatus = TradeOrderStatus.Rejected;
        if (item.note !== null) {
            this.rejectTradeOrder.note = item.note;
        }
        this.rejectTradeOrder.isSent = false;
        this.showRejectModal = true;
    };

    public closeReject = () => {
        this.rejectTradeOrder = null;
        this.clearUploadedFile();
        this.showRejectModal = false;
    };

    public submitReject = async () => {
        if (!this.rejectTradeOrder) return;
        const command = this.rejectTradeOrder;

        await this.taskRunner.run(async () => {
            if (this.uploadedFiles && this.uploadedFiles.length > 0 && !await this.tryUploadAttachment(command.tradeOrderId, this.uploadedFiles[0])) {
                return;
            }
            await this.TradeOrderApiClient.updateTradeOrder(command.toJSObject());
            await this.pageManager.refreshData();
            this.closeReject();
            NotifyUtils.addSuccess(TextConstants.Titles.Success, "Trade order rejected successfully.", 4);
        });
    };

    // ==================== REINSTATE MODAL ====================
    public showReinstate = (item: TradeOrderLookup) => {
        if (!this.canReinstate) {
            NotifyUtils.addWarning("Access Denied", "You do not have permission to reinstate trade orders.", 4);
            return;
        }

        this.reinstateTradeOrder = new UpdateTradeOrderCommand();
        this.reinstateTradeOrder.tradeOrderId = item.tradeOrderId;
        this.reinstateTradeOrder.tradeOrderStatus = TradeOrderStatus.Pending; // <-- Set to Reinstated
        this.reinstateTradeOrder.isSent = false;
        if (item.note !== null) {
            this.reinstateTradeOrder.note = item.note;
        }
        this.showReinstateModal = true;
    };

    public closeReinstate = () => {
        this.reinstateTradeOrder = null;
        this.showReinstateModal = false;
    };

    public submitReinstate = async () => {
        if (!this.reinstateTradeOrder) return;

        await this.taskRunner.run(async () => {
            await this.TradeOrderApiClient.updateTradeOrder(this.reinstateTradeOrder!.toJSObject());
            await this.pageManager.refreshData();
            this.closeReinstate();
            NotifyUtils.addSuccess(TextConstants.Titles.Success, "Trade order reinstated successfully.", 4);
        });
    };

    public viewTradeOrderAttachment = async (tradeOrderId: number) => {
        const downloadUrl = await this.taskRunner.waitForData(this.TradeOrderApiClient.downloadTradeOrderAttachment(tradeOrderId));
        window.open(downloadUrl, "Document");
    };

    // Navigation to home page
    public navigateToHome() {
        const currentPath: string = this.navigation.getCurrentViewPath();
    }

    // Loads all service providers for User organization for use in dropdowns
    public async loadAllServiceProviders() {
        try {
            const response = await this.serviceProviderApiClient.getServiceProviderFileGroupingListByCriteria(" ");
            this.allServiceProviders = response.data || [];
            response.data.forEach(provider => {
                this.serviceProviderLookup.set(
                    provider.staticServiceProviderFileGroupingId,
                    provider.serviceProviderGroupingName
                );
            });
        } catch (error: any) {
            NotifyUtils.addDanger(TextConstants.Titles.Error, `Failed to load service providers: ${error?.message || error}`, 4);
        }
    }

    public clearSearch() {
        // Reset each search criteria property to its default value
        this.tradeOrderSearchCriteria.meta.tradeDescription.value = "";
        this.tradeOrderSearchCriteria.meta.referenceNumber.value = "";
        const today = new Date();
        const priorDate = new Date().setDate(today.getDate() - 90);
        this.tradeOrderSearchCriteria.meta.fromDate.value = new Date(priorDate);
        this.tradeOrderSearchCriteria.meta.toDate.value = today;
        this.tradeOrderSearchCriteria.meta.tradeOrderStatusId.value = null;
        this.tradeOrderSearchCriteria.meta.staticServiceProviderFileGroupingID.value = null;
        // Refresh the grid data
        this.pageManager.refreshData();
    }

    public uploadedFile: { name: string; size: number; type: string } | null = null;
    public uploadFileType: 'confirm' | 'reject' | null = null;

    public clearUploadedFile = () => {
        this.uploadedFile = null;
        this.uploadFileType = null;
        this.uploadedFiles = undefined;
    };

    public tradeOrderAttachmentsManager = new Components.FileManager({
        multiple: false,
        allowedExtensions: [".pdf", ".jpg", ".jpeg", ".png", ".doc", ".docx"],
        onFilesSelected: this.handleFileSelection.bind(this)
    });

    public uploadedFiles: File[] | undefined;

    public handleFileSelection(fileList: File[] | FileList) {
        const file = Array.from(fileList)[0] ?? null;
        if (file) {
            // Validate file size (max 10MB)
            const maxSize = 10 * 1024 * 1024;
            if (file.size > maxSize) {
                ModalUtils.showMessage(
                    TextConstants.Titles.FileTooLarge,
                    TextConstants.Notifications.FileSizeExceedsLimit,
                );
                this.clearUploadedFile();
                return;
            }
        }
        this.uploadedFiles = Array.from(fileList);
    }

    private async tryUploadAttachment(tradeOrderId: number, file: File): Promise<boolean> {
        try {
            await this.TradeOrderApiClient.uploadTradeOrderAttachment(tradeOrderId, file);
            return true;
        } catch (error) {
            const uploadError = error as { message?: string };
            NotifyUtils.addWarning(
                TextConstants.Titles.AttachmentUploadFail,
                uploadError.message || TextConstants.Notifications.AttachmentUploadFailed,
                5
            );
            return false;
        }
    }
}